/**
 * Blog Service — API client for the Advocate Blog v1 backend
 * (`/api/blog/*`, see `lexomat_backend/server/api/advocate_blog.py`).
 *
 * Responsibilities:
 *   - Typed wrappers around the 11 `advocate_router` endpoints.
 *   - Translates the backend's `{detail: {code, ...}}` error shape into a
 *     single typed `BlogApiError` so UI code can branch on `err.code`
 *     instead of message-text heuristics.
 *   - Attaches the `X-Session-Id` header (read from a `session_id` cookie)
 *     on public view-counter calls so the backend's per-session throttle
 *     deduplicates anonymous views.
 */

import type {
  AttestationPayload,
  BlogAttestation,
} from '@/lib/blog/compliance';
import type {
  BlogEnabled,
  BlogError,
  BlogErrorCode,
  BlogPost,
  BlogPostListResponse,
  BlogPostLocale,
  BlogQuota,
  CreatePostRequest,
  UpdatePostRequest,
} from '@/types/blog';
import { BlogApiError } from '@/types/blog';
import ApiService, { type ApiResp } from './api-services';

// Re-export so consumers can `import BlogApiError from '@/services/blogService'`
// if they prefer the service-shaped import path.
export { BlogApiError };
// Re-export so consumers can `import { type BlogAttestation } from '@/services/blogService'`.
export type { BlogAttestation };

const BLOG_PATH = '/api/blog';

/** Read the `session_id` cookie. SSR-safe (returns undefined server-side). */
function readSessionIdCookie(): string | undefined {
  if (typeof document === 'undefined') return undefined;
  const target = 'session_id=';
  const cookies = document.cookie ? document.cookie.split(';') : [];
  for (const raw of cookies) {
    const cookie = raw.trim();
    if (cookie.startsWith(target)) {
      return decodeURIComponent(cookie.slice(target.length));
    }
  }
  return undefined;
}

/**
 * Inspect an apiClient response and throw a `BlogApiError` if it carries a
 * `{detail: {code, ...}}` shape. The `apiClient` response interceptor in
 * `api-services.ts` resolves with `error.response.data` for 4xx codes
 * (400/401/403-non-mfa/404), so the caller sees the FastAPI error envelope
 * directly — no exception is thrown by axios.
 *
 * 409 / 500 / 503 are *rejected* by the interceptor — those flow through
 * `throwIfAxiosDetail` below.
 */
function throwIfDetailEnvelope<T>(resp: ApiResp<T> | undefined): ApiResp<T> {
  if (resp === undefined || resp === null) {
    throw new BlogApiError('blog_not_found', 'Empty response from blog API');
  }
  if (typeof resp !== 'object') return resp;
  const maybeDetail = (resp as unknown as { detail?: unknown }).detail;
  if (maybeDetail && typeof maybeDetail === 'object') {
    throw detailToBlogError(maybeDetail as BlogError);
  }
  return resp;
}

/** Build a `BlogApiError` from a raw `{detail}` payload. */
function detailToBlogError(detail: BlogError): BlogApiError {
  const code = detail.code as BlogErrorCode;
  // exactOptionalPropertyTypes: omit keys whose value is undefined rather than
  // passing `undefined` into the Partial<BlogError> context.
  return new BlogApiError(code, detail.message, {
    ...(detail.used !== undefined ? { used: detail.used } : {}),
    ...(detail.cap !== undefined ? { cap: detail.cap } : {}),
    ...(detail.slug !== undefined ? { slug: detail.slug } : {}),
    ...(detail.server_rules_version !== undefined
      ? { server_rules_version: detail.server_rules_version }
      : {}),
  });
}

/**
 * Pull the FastAPI `{detail: {code, ...}}` envelope off an AxiosError
 * (raised by the interceptor for 409 / 500 / 503 / network failures)
 * and re-throw as `BlogApiError`.
 */
function rethrowAxiosAsBlogError(err: unknown): never {
  if (err && typeof err === 'object' && 'isAxiosError' in err) {
    const ax = err as { response?: { data?: { detail?: BlogError } } };
    const detail = ax.response?.data?.detail;
    if (detail && typeof detail === 'object' && 'code' in detail) {
      throw detailToBlogError(detail);
    }
  }
  throw err;
}

/** Typed empty-attestation sentinel for the obscure-existence 404 pattern. */
const EMPTY_ATTESTATIONS: ApiResp<{ items: BlogAttestation[] }> = {
  data: { items: [] },
  status: 'success',
  message: '',
  meta: {},
  errors: [],
  code: 200,
};

export class BlogService extends ApiService {
  private static instance: BlogService;

  constructor() {
    super('');
  }

  public static getInstance(): BlogService {
    if (!BlogService.instance) {
      BlogService.instance = new BlogService();
    }
    return BlogService.instance;
  }

  // ── /me/* ──────────────────────────────────────────────────────────────
  async listMyPosts(
    limit = 50,
    offset = 0
  ): Promise<ApiResp<BlogPostListResponse>> {
    try {
      const resp = await this.getData(
        `${BLOG_PATH}/me/posts?limit=${limit}&offset=${offset}`
      );
      return throwIfDetailEnvelope(resp);
    } catch (err) {
      rethrowAxiosAsBlogError(err);
    }
  }

  async createPost(body: CreatePostRequest): Promise<ApiResp<{ id: string }>> {
    try {
      const resp = await this.postData(`${BLOG_PATH}/me/posts`, body);
      return throwIfDetailEnvelope(resp);
    } catch (err) {
      rethrowAxiosAsBlogError(err);
    }
  }

  async getMyPost(postId: string): Promise<ApiResp<BlogPost>> {
    try {
      const resp = await this.getData(`${BLOG_PATH}/me/posts/${postId}`);
      return throwIfDetailEnvelope(resp);
    } catch (err) {
      rethrowAxiosAsBlogError(err);
    }
  }

  async updatePost(
    postId: string,
    body: UpdatePostRequest
  ): Promise<ApiResp<{ id: string }>> {
    try {
      const resp = await this.patchData(
        `${BLOG_PATH}/me/posts/${postId}`,
        body
      );
      return throwIfDetailEnvelope(resp);
    } catch (err) {
      rethrowAxiosAsBlogError(err);
    }
  }

  /**
   * Publish a draft post, gated behind the `PublishComplianceDialog`
   * attestation (#6653). The attestation payload (rules_version,
   * content_hash, content_bytes_length) is sent so the backend's
   * `publish_post_with_attestation` SQL function can persist the audit row
   * atomically with the publish state transition.
   */
  async publishPost(
    postId: string,
    attestation: AttestationPayload
  ): Promise<ApiResp<{ id: string; attestation_id: string }>> {
    try {
      const resp = await this.postData(
        `${BLOG_PATH}/me/posts/${postId}/publish`,
        attestation
      );
      return throwIfDetailEnvelope(resp);
    } catch (err) {
      rethrowAxiosAsBlogError(err);
    }
  }

  async unpublishPost(postId: string): Promise<ApiResp<{ id: string }>> {
    try {
      const resp = await this.postData(
        `${BLOG_PATH}/me/posts/${postId}/unpublish`,
        {}
      );
      return throwIfDetailEnvelope(resp);
    } catch (err) {
      rethrowAxiosAsBlogError(err);
    }
  }

  async deletePost(postId: string): Promise<ApiResp<{ id: string }>> {
    try {
      const resp = await this.deleteData(
        `${BLOG_PATH}/me/posts/${postId}`,
        null
      );
      return throwIfDetailEnvelope(resp);
    } catch (err) {
      rethrowAxiosAsBlogError(err);
    }
  }

  /**
   * Owner-self-read of the publish-attestation ledger for one of the
   * caller's posts (#6653 / legal-review C1). Returns `[]` (not an
   * error) when the backend signals 404 — the spec uses an
   * "obscure pre-launch existence" pattern so an attacker probing for
   * post ids learns nothing. 401/403 surface as `BlogApiError` so the
   * UI can branch on `err.code`.
   */
  async getMyPostAttestations(
    postId: string
  ): Promise<ApiResp<{ items: BlogAttestation[] }>> {
    try {
      const resp = await this.getData(
        `${BLOG_PATH}/me/posts/${postId}/attestations`
      );
      // 404 envelope (no detail, the interceptor resolves with the body
      // for non-rejected statuses — 404 here means the envelope's
      // `status === 'error'` branch) → empty list (obscure pre-launch
      // existence).
      if (
        resp &&
        typeof resp === 'object' &&
        'detail' in (resp as unknown as Record<string, unknown>)
      ) {
        const detail = (resp as unknown as { detail?: { code?: string } })
          .detail;
        if (detail?.code === 'blog_not_found') {
          return EMPTY_ATTESTATIONS;
        }
      }
      return throwIfDetailEnvelope(resp);
    } catch (err) {
      // Axios-layer 404 (no envelope) → also obscure.
      if (err && typeof err === 'object' && 'isAxiosError' in err) {
        const ax = err as { response?: { status?: number } };
        if (ax.response?.status === 404) {
          return EMPTY_ATTESTATIONS;
        }
      }
      rethrowAxiosAsBlogError(err);
    }
  }

  async getQuota(): Promise<ApiResp<BlogQuota>> {
    try {
      const resp = await this.getData(`${BLOG_PATH}/me/quota`);
      return throwIfDetailEnvelope(resp);
    } catch (err) {
      rethrowAxiosAsBlogError(err);
    }
  }

  /**
   * Whether the advocate blog editor is open to the caller.
   *
   * Unlike the authoring routes, this one answers `{ enabled: false }` instead
   * of 404-ing when the feature is off, so the settings nav can distinguish
   * "feature closed" from a transport failure.
   */
  async getEnabled(): Promise<ApiResp<BlogEnabled>> {
    try {
      const resp = await this.getData(`${BLOG_PATH}/me/enabled`);
      return throwIfDetailEnvelope(resp);
    } catch (err) {
      rethrowAxiosAsBlogError(err);
    }
  }

  // ── /public/* ──────────────────────────────────────────────────────────
  async listPublicPosts(
    professionalId: string,
    // `null` omits the locale filter → the author's posts in EVERY language
    // (used by the in-profile blog preview). A concrete locale keeps the
    // SEO-scoped /blog index and single-post reads language-specific.
    locale: BlogPostLocale | null = 'sk',
    limit = 50,
    offset = 0
  ): Promise<ApiResp<BlogPostListResponse>> {
    try {
      const localeParam = locale ? `&locale=${locale}` : '';
      const resp = await this.getData(
        `${BLOG_PATH}/public/professionals/${professionalId}/posts?limit=${limit}&offset=${offset}${localeParam}`
      );
      return throwIfDetailEnvelope(resp);
    } catch (err) {
      rethrowAxiosAsBlogError(err);
    }
  }

  async getPublicPost(
    professionalId: string,
    slug: string,
    locale: BlogPostLocale = 'sk'
  ): Promise<ApiResp<BlogPost>> {
    try {
      const resp = await this.getData(
        `${BLOG_PATH}/public/professionals/${professionalId}/posts/${slug}?locale=${locale}`
      );
      return throwIfDetailEnvelope(resp);
    } catch (err) {
      rethrowAxiosAsBlogError(err);
    }
  }

  /**
   * Bump the view counter for a public post. Anonymous; the backend dedupes
   * within a 1-hour TTL keyed by the `X-Session-Id` header (falls back to
   * peer IP when absent).
   */
  async incrementView(
    professionalId: string,
    slug: string,
    locale: BlogPostLocale = 'sk'
  ): Promise<ApiResp<{ counted: boolean }>> {
    const sessionId = readSessionIdCookie();
    const headers: Record<string, string> = {};
    if (sessionId) headers['X-Session-Id'] = sessionId;
    try {
      const resp = await this.postData(
        `${BLOG_PATH}/public/professionals/${professionalId}/posts/${slug}/view?locale=${locale}`,
        {},
        { headers }
      );
      return throwIfDetailEnvelope(resp);
    } catch (err) {
      rethrowAxiosAsBlogError(err);
    }
  }
}

const blogService = BlogService.getInstance();
export default blogService;
